import { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../../utils/api';
import { Button, Badge, Modal, Alert, Spinner, InfoRow, Input, Toggle } from './shared';

const STATE_VARIANTS = {
    active: 'success',
    building: 'info',
    pending: 'warning',
    error: 'error',
    build_failed: 'error',
};

const formatDate = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleDateString('vi-VN') + ' ' + d.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' });
};

const DEFAULT_LIMIT = 20;

export default function AdminSnapshots() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);

    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await adminApi.listSnapshots({ limit: DEFAULT_LIMIT });
            setItems(res.snapshots || []);
            setTotal(res.total || 0);
        } catch (err) {
            setError(err.message || 'Failed to load snapshots');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadData(); }, [loadData]);

    const handleViewItem = async (name) => {
        try {
            const item = await adminApi.getSnapshot(name);
            setSelectedItem(item);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleDelete = async (name) => {
        if (!confirm(`Delete snapshot "${name}"? This cannot be undone.`)) return;
        try {
            await adminApi.deleteSnapshot(name);
            setSuccessMessage(`Deleted "${name}"`);
            setSelectedItem(null);
            setItems(prev => prev.filter(i => i.name !== name));
            setTotal(prev => Math.max(0, prev - 1));
            setTimeout(() => setSuccessMessage(null), 5000);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleCreated = (snapshot) => {
        setShowCreateModal(false);
        setSuccessMessage(`Snapshot "${snapshot.name}" created (state: ${snapshot.state})`);
        setTimeout(() => setSuccessMessage(null), 5000);
        loadData();
    };

    return (
        <div>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                <div>
                    <h1 className="text-lg font-semibold text-gray-900">Sandbox Snapshots</h1>
                    <p className="text-sm text-gray-500 mt-0.5">
                        {total} snapshot{total !== 1 ? 's' : ''} — pre-built sandbox images
                    </p>
                </div>
                <Button variant="primary" size="sm" onClick={() => setShowCreateModal(true)}>
                    Create Snapshot
                </Button>
            </div>

            {error && <Alert variant="error" onClose={() => setError(null)} className="mb-4">{error}</Alert>}
            {successMessage && <Alert variant="success" onClose={() => setSuccessMessage(null)} className="mb-4">{successMessage}</Alert>}

            <div className="flex flex-wrap items-center gap-2 mb-4">
                <Button variant="ghost" size="xs" onClick={loadData}>Refresh</Button>
            </div>

            {loading ? (
                <Spinner size="lg" text="Loading..." center />
            ) : items.length === 0 ? (
                <div className="text-center py-12 text-gray-500 text-sm">
                    No snapshots found. Create one to pre-install financial libraries.
                </div>
            ) : (
                <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-gray-200 bg-gray-50/60">
                                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Name</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500">Image</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500 w-24">State</th>
                                <th className="text-right px-3 py-2.5 text-xs font-medium text-gray-500 w-16">CPU</th>
                                <th className="text-right px-3 py-2.5 text-xs font-medium text-gray-500 w-20">Memory</th>
                                <th className="text-left px-3 py-2.5 text-xs font-medium text-gray-500 w-32">Created</th>
                                <th className="text-right px-4 py-2.5 text-xs font-medium text-gray-500 w-28">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {items.map((item) => (
                                <tr
                                    key={item.id}
                                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                                    onClick={() => handleViewItem(item.name)}
                                >
                                    <td className="px-4 py-2.5">
                                        <span className="font-mono text-xs font-medium text-gray-900">{item.name}</span>
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <span className="text-xs text-gray-600 truncate block max-w-xs">{item.image_name || '-'}</span>
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <Badge variant={STATE_VARIANTS[item.state?.toLowerCase()] || 'outline'}>
                                            {item.state}
                                        </Badge>
                                    </td>
                                    <td className="px-3 py-2.5 text-right">
                                        <span className="text-xs text-gray-500 font-mono">{item.cpu ?? '-'}</span>
                                    </td>
                                    <td className="px-3 py-2.5 text-right">
                                        <span className="text-xs text-gray-500 font-mono">{item.memory != null ? `${item.memory} GB` : '-'}</span>
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <span className="text-xs text-gray-500">{formatDate(item.created_at)}</span>
                                    </td>
                                    <td className="px-4 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                                        <div className="flex items-center justify-end gap-1">
                                            <Button variant="ghost" size="xs" onClick={() => handleViewItem(item.name)}>
                                                View
                                            </Button>
                                            <Button
                                                variant="ghost" size="xs"
                                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                onClick={() => handleDelete(item.name)}
                                            >
                                                Delete
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {selectedItem && (
                <SnapshotDetailModal
                    item={selectedItem}
                    onClose={() => setSelectedItem(null)}
                    onDelete={() => handleDelete(selectedItem.name)}
                />
            )}

            {showCreateModal && (
                <CreateSnapshotModal
                    onClose={() => setShowCreateModal(false)}
                    onCreated={handleCreated}
                    onError={setError}
                />
            )}
        </div>
    );
}

function SnapshotDetailModal({ item, onClose, onDelete }) {
    return (
        <Modal open onClose={onClose} title={item.name} size="lg">
            <Modal.Body>
                <div className="grid grid-cols-2 gap-x-8 gap-y-0">
                    <InfoRow label="ID" value={item.id} mono />
                    <InfoRow label="Name" value={item.name} />
                    <InfoRow label="Image" value={item.image_name} />
                    <InfoRow label="State" value={item.state} />
                    <InfoRow label="CPU" value={item.cpu} />
                    <InfoRow label="Memory" value={item.memory != null ? `${item.memory} GB` : '-'} />
                    <InfoRow label="Disk" value={item.disk != null ? `${item.disk} GB` : '-'} />
                    <InfoRow label="Created" value={formatDate(item.created_at)} />
                    <InfoRow label="Updated" value={formatDate(item.updated_at)} />
                </div>

                {item.error_reason && (
                    <div className="mt-4">
                        <p className="text-sm font-medium text-red-700 mb-2">Error</p>
                        <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
                            {item.error_reason}
                        </p>
                    </div>
                )}

                <div className="mt-4 flex justify-end gap-2">
                    <Button
                        variant="ghost" size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={onDelete}
                    >
                        Delete Snapshot
                    </Button>
                </div>
            </Modal.Body>
        </Modal>
    );
}

function CreateSnapshotModal({ onClose, onCreated, onError }) {
    const [name, setName] = useState('');
    const [pythonVersion, setPythonVersion] = useState('3.12');
    const [useDefault, setUseDefault] = useState(true);
    const [extraPackages, setExtraPackages] = useState('');
    const [cpu, setCpu] = useState('');
    const [memory, setMemory] = useState('');
    const [disk, setDisk] = useState('');
    const [creating, setCreating] = useState(false);
    const [defaultPackages, setDefaultPackages] = useState([]);

    useEffect(() => {
        adminApi.getDefaultPackages()
            .then(res => setDefaultPackages(res.packages || []))
            .catch(() => {});
    }, []);

    const handleSubmit = async () => {
        if (!name.trim()) return;
        setCreating(true);
        try {
            const extra = extraPackages
                .split(/[,\n]/)
                .map(p => p.trim())
                .filter(Boolean);
            const data = {
                name: name.trim(),
                python_version: pythonVersion,
                use_default_financial: useDefault,
                extra_packages: extra,
            };
            if (cpu) data.cpu = parseInt(cpu, 10);
            if (memory) data.memory = parseInt(memory, 10);
            if (disk) data.disk = parseInt(disk, 10);
            const snapshot = await adminApi.createSnapshot(data);
            onCreated(snapshot);
        } catch (err) {
            onError(err.message || 'Failed to create snapshot');
            onClose();
        } finally {
            setCreating(false);
        }
    };

    const selectCls = "h-9 px-3 pr-8 rounded-lg border border-gray-200 bg-white text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22%239ca3af%22%20stroke-width%3D%222%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')] bg-[length:12px] bg-[right_8px_center] bg-no-repeat";

    return (
        <Modal open onClose={onClose} title="Create Snapshot" size="lg">
            <Modal.Body>
                {creating ? (
                    <div className="py-12">
                        <Spinner size="lg" text="Building snapshot... This may take 5-10 minutes." center />
                    </div>
                ) : (
                    <div className="space-y-4">
                        <Input
                            label="Snapshot Name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="simplize-financial-python"
                        />

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Python Version</label>
                            <select value={pythonVersion} onChange={(e) => setPythonVersion(e.target.value)} className={selectCls}>
                                <option value="3.9">3.9</option>
                                <option value="3.10">3.10</option>
                                <option value="3.11">3.11</option>
                                <option value="3.12">3.12</option>
                                <option value="3.13">3.13</option>
                            </select>
                        </div>

                        <div className="flex items-center gap-3">
                            <Toggle
                                checked={useDefault}
                                onChange={setUseDefault}
                            />
                            <span className="text-sm text-gray-700">Include default financial packages ({defaultPackages.length})</span>
                        </div>

                        {useDefault && defaultPackages.length > 0 && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Default Packages</label>
                                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 max-h-32 overflow-y-auto">
                                    <div className="flex flex-wrap gap-1.5">
                                        {defaultPackages.map(pkg => (
                                            <span key={pkg} className="inline-block px-2 py-0.5 bg-white border border-gray-200 rounded text-xs text-gray-600">
                                                {pkg}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Extra Packages</label>
                            <textarea
                                value={extraPackages}
                                onChange={(e) => setExtraPackages(e.target.value)}
                                placeholder="tensorflow, chess, plotly (comma or newline separated)"
                                rows={3}
                                className="w-full px-3 py-2 rounded-lg border border-gray-200 bg-white text-xs text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 font-mono resize-none"
                            />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                            <Input label="CPU (cores)" value={cpu} onChange={(e) => setCpu(e.target.value)} placeholder="4" type="number" />
                            <Input label="Memory (GB)" value={memory} onChange={(e) => setMemory(e.target.value)} placeholder="8" type="number" />
                            <Input label="Disk (GB)" value={disk} onChange={(e) => setDisk(e.target.value)} placeholder="20" type="number" />
                        </div>

                        <div className="flex justify-end gap-2 pt-2">
                            <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
                            <Button variant="primary" size="sm" onClick={handleSubmit} disabled={!name.trim()}>
                                Create Snapshot
                            </Button>
                        </div>
                    </div>
                )}
            </Modal.Body>
        </Modal>
    );
}
